import { FC, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, useParams } from 'react-router-dom';

import { ERROR } from 'constants/paths';
import { isEmpty } from 'lodash';
import { useGetPublicOnePagePlan } from 'services/businessPlan/hooks/useGetPublicOnePagePlan';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getBusinessToolStoreClear,
  getSetBusinessPlan,
  getSetBusinessPlanChapters,
} from 'store/useBusinessToolStore/selectors';

import { SkeletonLoader } from 'components/_basic/SkeletonLoader';
import { useGetInitialValues } from 'components/BusinessToolForm/utils/form';
import { OnePagePreviewContainer } from 'components/OnePageTemplates';

import { getFormValueFromChapters } from 'helpers/businessTool';
import { insertCode } from 'helpers/path';

export const OnePageSharedPage: FC = () => {
  const { id = '' } = useParams();
  const localizedInitialValues = useGetInitialValues();

  const setBusinessPlan = useBusinessToolStore(getSetBusinessPlan);
  const setBusinessPlanChapters = useBusinessToolStore(getSetBusinessPlanChapters);
  const clearBusinessToolStore = useBusinessToolStore(getBusinessToolStoreClear);

  const { data, error, isLoading } = useGetPublicOnePagePlan(id);
  const lastHydratedDataRef = useRef<typeof data>();

  useEffect(() => {
    if (data && lastHydratedDataRef.current !== data) {
      setBusinessPlan(data.plan);
      setBusinessPlanChapters(getFormValueFromChapters(data.chapters, localizedInitialValues));
      lastHydratedDataRef.current = data;
    }

    return () => clearBusinessToolStore();
  }, [clearBusinessToolStore, data, localizedInitialValues, setBusinessPlan, setBusinessPlanChapters]);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!isEmpty(error)) {
    return <Navigate to={insertCode(ERROR, '404')} replace />;
  }

  return (
    <>
      <Helmet>
        <script type="text/javascript" defer>
          {`
            window.dataLayer = window.dataLayer || [];
            let userId = '';
            try {
              const userStore = localStorage.getItem('user-store');
              if (userStore) {
                const parsed = JSON.parse(userStore);
                userId = parsed?.state?.authUser?.id || '';
                dataLayer.push({ user_id: userId });
              }
            } catch (e) {
              console.error('Failed to parse user-store:', e);
            }
          `}
        </script>
      </Helmet>
      <OnePagePreviewContainer />
    </>
  );
};
